import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TBResponse } from '@construction/models/wbs-elements.model';
import { InvitationSentDataType, UserInterface } from '@core/interfaces/user.interface';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export type RegisterPayload = {
  firstName: string;
  lastName: string;
  title: string;
  invitationCode: string;
  password: string | null;
};

@Injectable({ providedIn: 'root' })
export class UserApiService {
  private http = inject(HttpClient);

  public fetchCurrentUser() {
    return this.http.get<TBResponse<UserInterface>>(`${environment.apiUrl}/${environment.apiVersion}/user/`);
  }

  public updateUser(formData: FormData, userId: string, deleteProfileImage = false) {
    const params = new HttpParams({
      fromObject: {
        delete_profile_picture: deleteProfileImage,
      },
    });

    return this.http.patch(`${environment.apiUrl}/${environment.apiVersion}/users/${userId}/`, formData, { params });
  }

  public register(payload: RegisterPayload) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/sign_up/`;

    return this.http.post(url, {
      first_name: payload.firstName,
      last_name: payload.lastName,
      position: payload.title,
      password: payload.password,
      invitation_code: payload.invitationCode,
    });
  }

  public requestPasswordReset(email: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/initiate_password_reset/`;

    return this.http.post(url, { email });
  }

  public resetPassword(password: string, passCode: string) {
    const url = `${environment.apiUrl}/${environment.apiVersion}/reset_password/${passCode}/`;

    return this.http.post(url, { password });
  }

  public getPasswordResetEmail(passCode: string): Observable<TBResponse<{ email: string }>> {
    const url = `${environment.apiUrl}/${environment.apiVersion}/reset_password/${passCode}/email/`;

    return this.http.get<TBResponse<{ email: string }>>(url);
  }

  public getInvitation(code: string): Observable<InvitationSentDataType> {
    return this.http.get<InvitationSentDataType>(
      `${environment.apiUrl}/${environment.apiVersion}/public/invitations/${code}/`,
    );
  }

  public getInvitations() {
    return this.http.get(`${environment.apiUrl}/${environment.apiVersion}/invitations/`);
  }

  public getUsers(onlySuperAdmins = false) {
    let params = new HttpParams();
    params = params.append('superadmin', onlySuperAdmins);

    return this.http
      .get<TBResponse<UserInterface[]>>(`${environment.apiUrl}/${environment.apiVersion}/users/`, {
        params,
      })
      .pipe(map((res) => res.data));
  }

  public changeUserAvailability(disabled: boolean, userId: string) {
    return this.http.put(`${environment.apiUrl}/${environment.apiVersion}/users/${userId}/change_enablement/`, {
      disabled: !disabled,
    });
  }
}
